import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import AdminNav from './AdminNav';
import { FaChartBar, FaEye, FaSync } from 'react-icons/fa';

const Container = styled.div`
  max-width: 1200px;
  margin: 2rem auto;
  padding: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const HeaderLeft = styled.div``;

const Title = styled.h1`
  color: #333;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background-color: #0d6efd;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;
  
  &:hover {
    background-color: #0b5ed7;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${props => props.color || '#0d6efd'};
`;

const StatLabel = styled.div`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  color: #333;
  font-size: 2rem;
  font-weight: 700;
`;

const PagesList = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const PagesTitle = styled.h2`
  color: #333;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: #f5f5f5;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: #f9f9f9;
  }
`;

const TableHeader = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #555;
`;

const TableCell = styled.td`
  padding: 1rem;
  color: #333;
`;

const PagePath = styled.div`
  font-weight: 500;
  color: #0d6efd;
`;

const ViewCount = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
  font-size: 1.1rem;
`;

const PageAnalytics = () => {
  const [pageViews, setPageViews] = useState({});
  const [loading, setLoading] = useState(true);
  const [totalViews, setTotalViews] = useState(0);

  const fetchPageViews = async () => {
    setLoading(true);
    try {
      const analyticsRef = doc(db, 'analytics', 'pageViews');
      const analyticsDoc = await getDoc(analyticsRef);
      
      if (analyticsDoc.exists()) {
        const data = analyticsDoc.data();
        const pages = data.pages || {};
        
        // Convert underscore-separated paths back to forward slashes
        const formattedPages = {};
        let total = 0;
        
        Object.entries(pages).forEach(([key, value]) => {
          const path = key.replace(/_/g, '/');
          formattedPages[path] = value;
          total += value;
        });
        
        setPageViews(formattedPages);
        setTotalViews(total);
      } else {
        setPageViews({});
        setTotalViews(0);
      }
    } catch (error) {
      console.error('Error fetching page views:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPageViews();
  }, []);

  // Get page name from path
  const getPageName = (path) => {
    if (path === '/') return 'Home Page';
    if (path === '/categories') return 'Categories Page';
    if (path === '/about') return 'About Page';
    if (path === '/contact') return 'Contact Page';
    if (path === '/treasure-hunt') return 'Treasure Hunt';
    if (path === '/leaderboard') return 'Leaderboard';
    if (path.startsWith('/product/')) return 'Product Page';
    if (path.startsWith('/admin')) return 'Admin Panel';
    return path;
  };

  // Sort pages by view count
  const sortedPages = Object.entries(pageViews).sort((a, b) => b[1] - a[1]);

  return (
    <Container>
      <AdminNav />
      
      <Header>
        <HeaderLeft>
          <Title>
            <FaChartBar /> Page Analytics
          </Title>
          <Subtitle>Track page visits across your website</Subtitle>
        </HeaderLeft>
        <RefreshButton onClick={fetchPageViews} disabled={loading}>
          <FaSync /> Refresh
        </RefreshButton>
      </Header>

      <StatsGrid>
        <StatCard color="#0d6efd">
          <StatLabel>Total Page Views</StatLabel>
          <StatValue>{totalViews.toLocaleString()}</StatValue>
        </StatCard>
        
        <StatCard color="#28a745">
          <StatLabel>Unique Pages</StatLabel>
          <StatValue>{Object.keys(pageViews).length}</StatValue>
        </StatCard>
        
        <StatCard color="#ffc107">
          <StatLabel>Most Visited</StatLabel>
          <StatValue style={{ fontSize: '1.2rem' }}>
            {sortedPages.length > 0 ? getPageName(sortedPages[0][0]) : 'N/A'}
          </StatValue>
        </StatCard>
      </StatsGrid>

      <PagesList>
        <PagesTitle>
          <FaEye /> Page Views by URL
        </PagesTitle>
        
        {loading ? (
          <LoadingState>Loading analytics data...</LoadingState>
        ) : sortedPages.length > 0 ? (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Page</TableHeader>
                <TableHeader>Path</TableHeader>
                <TableHeader>Views</TableHeader>
                <TableHeader>Percentage</TableHeader>
              </TableRow>
            </TableHead>
            <tbody>
              {sortedPages.map(([path, views]) => (
                <TableRow key={path}>
                  <TableCell>
                    <strong>{getPageName(path)}</strong>
                  </TableCell>
                  <TableCell>
                    <PagePath>{path}</PagePath>
                  </TableCell>
                  <TableCell>
                    <ViewCount>
                      <FaEye color="#0d6efd" />
                      {views.toLocaleString()}
                    </ViewCount>
                  </TableCell>
                  <TableCell>
                    {((views / totalViews) * 100).toFixed(1)}%
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyState>
            No page view data available yet. Page views will be tracked as users visit your site.
          </EmptyState>
        )}
      </PagesList>
    </Container>
  );
};

export default PageAnalytics;
